/*
 * Ascrono cart API (cart workstream). Contract: .build/contracts/cart.md
 *
 * window.ascCart.add(items, {open, source}) · update(key, qty) · remove(key) · setCover(dockVariantId, years)
 * replaceDocks(key, newVariantId) · get() · open() · close() · refresh()
 * Events on document: asc:cart:ready · asc:cart:updated · asc:cart:open · asc:cart:close · asc:cart:error
 *
 * Warranty rule: the 2-year cover (variant cfg.warrantyId, never by title) is one line per dock variant,
 * tagged with the hidden property _asc_dock = <dock variant id>; its quantity always equals that dock
 * variant's quantity in the cart (dock = SKU starting "DS-"). planSync() computes the fix-ups; it runs
 * after every change, whoever made it (we also watch fetch/XHR calls to /cart/*).
 */
(function () {
  'use strict';
  if (window.ascCart && window.ascCart.__asc) return;

  var cfg = window.ascCartConfig || {};
  var W_ID = String(cfg.warrantyId || 45072952623268);
  var ROOT = (cfg.root || (window.Shopify && Shopify.routes && Shopify.routes.root) || '/').replace(/\/?$/, '/');
  var MARK = 'X-Asc-Cart';
  var nativeFetch = window.fetch.bind(window);

  /* ---------- pure helpers (also used by the QA tests) ---------- */

  // Discontinued Type A/B docks (by SKU) never get a cover.
  var OLD_DOCK = /^(DS-L-TA|DS-L-TB|DS-MP5-R1$|DS-MP9-H-R1$|DS-SM-H60-MP5-R1$)/;
  function isDock(item) {
    return !!item && typeof item.sku === 'string' && item.sku.indexOf('DS-') === 0 && !OLD_DOCK.test(item.sku);
  }
  function isCover(item) {
    return !!item && String(item.variant_id) === W_ID;
  }
  function dockLabel(item, names) {
    var core = (item.sku || '').slice(0, 7);
    return ((names && names[core]) || item.product_title || 'Docking station') + ' dock';
  }
  function coverProps(dockId, label) {
    return { _asc_dock: String(dockId), Covers: label };
  }

  /**
   * planSync(cart, {removeCovers}) → {updates: {lineKey: qty}, adds: []}
   * Only cover lines tagged _asc_dock are managed (the cart's own per-dock cover):
   * - a tagged line gets its dock variant's total quantity (0 when the dock is gone);
   * - duplicate tagged lines for the same dock are merged into the first one;
   * - where the market doesn't sell the warranty (removeCovers) tagged lines are removed.
   * Untagged warranty lines are left alone: the warranty also sells on its own after the dock order
   * (Klaviyo upsell emails → warranty product page → Add to cart, no dock in the cart).
   */
  function planSync(cart, o) {
    o = o || {};
    var docks = {};
    var items = (cart && cart.items) || [];
    items.forEach(function (it) {
      if (isDock(it)) {
        var k = String(it.variant_id);
        docks[k] = (docks[k] || 0) + it.quantity;
      }
    });
    var updates = {};
    var seen = {};
    items.forEach(function (it) {
      if (!isCover(it)) return;
      var d = it.properties && it.properties._asc_dock;
      if (!d) return; // bought on its own: not ours to manage
      if (o.removeCovers) {
        updates[it.key] = 0;
        return;
      }
      d = String(d);
      var want = seen[d] ? 0 : docks[d] || 0;
      seen[d] = true;
      if (it.quantity !== want) updates[it.key] = want;
    });
    return { updates: updates, adds: [] };
  }

  /** Sums used by tests and the live region: products shown (warranty excluded), docks, cover units. */
  function summarize(cart) {
    var s = { products: 0, docks: 0, covers: 0, total: cart ? cart.total_price : 0 };
    ((cart && cart.items) || []).forEach(function (it) {
      if (isCover(it) && it.properties && it.properties._asc_dock) s.covers += it.quantity;
      else s.products += it.quantity;
      if (isDock(it)) s.docks += it.quantity;
    });
    return s;
  }

  /* ---------- network ---------- */

  function req(path, body) {
    var headers = { Accept: 'application/json' };
    headers[MARK] = '1';
    if (body) headers['Content-Type'] = 'application/json';
    return timedFetch(ROOT + path, {
      method: body ? 'POST' : 'GET',
      headers: headers,
      credentials: 'same-origin',
      body: body ? JSON.stringify(body) : undefined,
    }, 45000).then(function (r) {
      return r
        .json()
        .catch(function () {
          return {};
        })
        .then(function (j) {
          if (!r.ok || (j && j.status && Number(j.status) >= 400)) {
            var e = new Error((j && (j.description || j.message)) || 'Something went wrong. Please try again.');
            e.status = r.status;
            e.data = j;
            throw e;
          }
          return j;
        });
    });
  }
  function getCart() {
    // A bot-challenge or proxy page can answer 200 without cart JSON: treat that as a failed read, never as a cart.
    return req('cart.js').then(function (c) {
      if (!c || !Array.isArray(c.items)) {
        var e = new Error("That didn't work. Please check your connection and try again.");
        e.network = true;
        throw e;
      }
      return c;
    });
  }

  // fetch that gives up after ms (a hung request must never block the cart)
  function timedFetch(url, init, ms) {
    var ctl = window.AbortController ? new AbortController() : null;
    var t = ctl ? setTimeout(function () { ctl.abort(); }, ms) : null;
    return nativeFetch(url, Object.assign({}, init, ctl ? { signal: ctl.signal } : {})).then(
      function (r) { clearTimeout(t); return r; },
      function (e) {
        clearTimeout(t);
        // network failure or our own timeout: never show the browser's raw text to shoppers
        var err = new Error("That didn't work. Please check your connection and try again.");
        err.cause = e;
        err.network = true;
        throw err;
      }
    );
  }
  function within(p, ms) {
    return Promise.race([p, new Promise(function (ok) { setTimeout(ok, ms); })]);
  }

  // One mutation at a time, in call order.
  var chain = Promise.resolve();
  function serial(fn) {
    var p = chain.then(fn, fn);
    chain = p.catch(function () {});
    return p;
  }

  function emit(name, detail) {
    document.dispatchEvent(new CustomEvent(name, { detail: detail || {} }));
  }

  function syncOpts() {
    return {
      coverOffered: !!cfg.coverOffered,
      removeCovers: cfg.coverOffered === false, // fr1: only where the market doesn't sell the warranty
      names: cfg.names,
    };
  }

  function applyPlan(plan) {
    var p = Promise.resolve();
    if (Object.keys(plan.updates).length) {
      p = p.then(function () {
        return req('cart/update.js', { updates: plan.updates });
      });
    }
    if (plan.adds.length) {
      p = p.then(function () {
        return req('cart/add.js', { items: plan.adds });
      });
    }
    return p;
  }

  // Runs planSync until nothing is left to fix (max 3 rounds). Resolves with the final cart.
  function sync(cart) {
    var rounds = 0;
    function step(c) {
      var plan = planSync(c, syncOpts());
      if (!Object.keys(plan.updates).length && !plan.adds.length) return c;
      if (++rounds > 3) return c;
      return applyPlan(plan).then(getCart).then(step);
    }
    return (cart ? Promise.resolve(cart) : getCart()).then(step);
  }

  /* ---------- rendering (Section Rendering API) ---------- */

  function bodies() {
    return Array.prototype.slice.call(document.querySelectorAll('[data-asc-cart-body]'));
  }

  function sectionIds() {
    var ids = [];
    if (document.getElementById('AscCartDrawer')) ids.push('cart-drawer');
    if (document.getElementById('cart-icon-bubble')) ids.push('cart-icon-bubble');
    return ids;
  }

  function pageSectionId() {
    var el = document.querySelector('[data-asc-cart-section]');
    return el ? el.getAttribute('data-asc-cart-section') : null;
  }

  function fetchSections() {
    var ids = sectionIds();
    var pid = pageSectionId();
    var jobs = [];
    if (ids.length) {
      jobs.push(
        // No Accept: application/json here: on /cart Shopify then answers with cart.js instead of the sections.
        timedFetch(ROOT + '?sections=' + ids.join(','), { credentials: 'same-origin' }, 10000)
          .then(function (r) {
            return r.json();
          })
      );
    }
    if (pid) {
      jobs.push(
        timedFetch(window.location.pathname + '?sections=' + encodeURIComponent(pid), { credentials: 'same-origin' }, 10000).then(function (r) {
          return r.json();
        })
      );
    }
    return Promise.all(jobs).then(function (res) {
      res.forEach(function (r) {
        if (r && (r.items || r.token)) console.warn('[ascCart] got cart JSON instead of sections');
      });
      return Object.assign.apply(Object, [{}].concat(res));
    });
  }

  function focusKey() {
    var a = document.activeElement;
    if (!a || !a.closest || !a.closest('[data-asc-cart-body]')) return null;
    return { key: a.getAttribute('data-asc-focus'), mode: a.closest('[data-asc-cart-body]').getAttribute('data-mode') };
  }

  function swapBody(oldBody, newBody) {
    var scroller = oldBody.querySelector('[data-asc-scroll]');
    var top = scroller ? scroller.scrollTop : 0;
    oldBody.className = newBody.className;
    if (pending > 0) oldBody.classList.add('is-busy');
    ['data-count', 'data-total', 'data-currency'].forEach(function (a) {
      if (newBody.hasAttribute(a)) oldBody.setAttribute(a, newBody.getAttribute(a));
    });
    newBody.querySelectorAll('[data-ascc-region]').forEach(function (nr) {
      var name = nr.getAttribute('data-ascc-region');
      var or = oldBody.querySelector('[data-ascc-region="' + name + '"]');
      if (!or) return;
      // Wallet buttons are kept once rendered (re-creating them flickers); filled when they were empty.
      if (or.hasAttribute('data-ascc-keep')) {
        // the cart page renders its own wallets; never load them a second time in the drawer there
        if (cfg.isCartPage && oldBody.getAttribute('data-mode') === 'drawer') return;
        // keep rendered wallet buttons (re-creating them flickers); clear them when the cart empties
        if (newBody.classList.contains('is-empty')) or.innerHTML = '';
        else if (!or.children.length) or.innerHTML = nr.innerHTML;
        return;
      }
      or.innerHTML = nr.innerHTML;
    });
    var sc = oldBody.querySelector('[data-asc-scroll]');
    if (sc) sc.scrollTop = top;
    enhance(oldBody);
  }

  var rendering = null;
  var renderAgain = false;
  function render() {
    if (rendering) {
      renderAgain = true;
      return rendering;
    }
    var fk = null;
    rendering = fetchSections()
      .then(function (sections) {
        // where focus is NOW (the drawer may have focused its heading while the request ran), restored after the swap
        fk = focusKey();
        Object.keys(sections).forEach(function (id) {
          var html = sections[id];
          if (!html) return;
          var doc = new DOMParser().parseFromString(html, 'text/html');
          if (id === 'cart-icon-bubble') {
            var bubble = document.getElementById('cart-icon-bubble');
            var src = doc.querySelector('.shopify-section');
            if (bubble && src) bubble.innerHTML = src.innerHTML;
            return;
          }
          doc.querySelectorAll('[data-asc-cart-body]').forEach(function (nb) {
            var mode = nb.getAttribute('data-mode');
            bodies().forEach(function (ob) {
              if (ob.getAttribute('data-mode') === mode) swapBody(ob, nb);
            });
          });
        });
        var drawer = document.getElementById('AscCartDrawer');
        var body = document.querySelector('[data-asc-cart-body][data-mode="drawer"]');
        if (drawer && body) drawer.classList.toggle('is-empty', body.classList.contains('is-empty'));
        if (fk) restoreFocus(fk);
        bindUI();
      })
      .catch(function (e) {
        console.warn('[ascCart] render failed', e);
      })
      .then(function () {
        rendering = null;
        if (renderAgain) {
          renderAgain = false;
          return render();
        }
      });
    return rendering;
  }

  function restoreFocus(fk) {
    var body = document.querySelector('[data-asc-cart-body][data-mode="' + fk.mode + '"]');
    if (!body) return;
    var el = fk.key && body.querySelector('[data-asc-focus="' + fk.key + '"]');
    if (!el || el.disabled) el = body.querySelector('.ascc-title');
    if (el) el.focus({ preventScroll: !!(fk.key) });
  }

  function announce(msg) {
    bodies().forEach(function (b) {
      var live = b.querySelector('[data-asc-live]');
      if (live) {
        live.textContent = '';
        setTimeout(function () {
          live.textContent = msg;
        }, 50);
      }
    });
  }

  /* ---------- client-side bits of the rendered body ---------- */

  function money(cents, currency) {
    var v = cents / 100;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: v % 1 === 0 ? 0 : 2,
        maximumFractionDigits: 2,
      }).format(v);
    } catch (e) {
      return v.toFixed(2) + ' ' + currency;
    }
  }

  // Free-shipping bar for markets whose currency is not the shop currency.
  function fillShipBar(root) {
    root.querySelectorAll('[data-asc-ship]').forEach(function (bar) {
      if (bar.getAttribute('data-same-currency') === 'true') return;
      var rate = Number(window.Shopify && Shopify.currency && Shopify.currency.rate) || 0;
      var cur = bar.getAttribute('data-currency');
      if (!rate || !cur) return; // stay hidden rather than guess
      var threshold = Math.ceil((Number(bar.getAttribute('data-threshold-shop')) * rate) / 100) * 100; // whole units, rounded up
      var total = Number(bar.getAttribute('data-total'));
      var free = total >= threshold;
      var msg = bar.querySelector('[data-asc-ship-msg]');
      var note = bar.querySelector('[data-asc-ship-note]');
      var fill = bar.querySelector('[data-asc-ship-fill]');
      bar.classList.toggle('is-free', free);
      if (msg)
        msg.innerHTML = free
          ? '<span class="ascc-bar__tick" aria-hidden="true"><svg viewBox="0 0 16 16" width="12" height="12"><path d="M3.5 8.5l3 3 6-7" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg></span><strong>You get free shipping</strong>'
          : '<strong><span class="ascc-nb">' + money(threshold - total, cur) + '</span> away from free shipping</strong>';
      if (note)
        note.innerHTML =
          'Orders over <span class="ascc-nb">' + money(threshold, cur) + '</span> ship free ' + bar.getAttribute('data-where') + '.';
      if (fill) fill.style.width = Math.min(100, Math.round((total / threshold) * 100)) + '%';
      bar.hidden = false;
    });
  }

  // c-edd writes "Estimated between: Wed, 21st Oct and Mon, 26th Oct" at add time; hide it once it is stale.
  function checkEstimate(root) {
    var months = { jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5, jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11 };
    root.querySelectorAll('[data-asc-edd]').forEach(function (el) {
      var m = el.textContent.match(/(\d{1,2})(?:st|nd|rd|th)?\s+([A-Za-z]{3})/);
      if (!m) return;
      var now = new Date();
      var mon = months[m[2].toLowerCase()];
      if (mon === undefined) return;
      var d = new Date(now.getFullYear(), mon, Number(m[1]));
      if (d - now > 200 * 864e5) d.setFullYear(d.getFullYear() - 1); // Dec → Jan wrap
      var today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      if (d < today) el.hidden = true;
    });
  }

  function enhance(root) {
    fillShipBar(root);
    checkEstimate(root);
  }

  /* ---------- public operations ---------- */

  // Counts operations in flight (queued ones included); the cart is marked busy until the last one ends.
  var pending = 0;
  function busy(on) {
    pending = Math.max(0, pending + (on ? 1 : -1));
    var b = pending > 0;
    bodies().forEach(function (el) {
      el.classList.toggle('is-busy', b);
      el.setAttribute('aria-busy', b ? 'true' : 'false');
      // While a change is in flight Checkout is disabled (a disabled button fires no click), so nobody can leave for
      // checkout before the 2-year cover quantities are re-checked. Our own guard; no app dependency.
      el.querySelectorAll('.ascc-btn--checkout').forEach(function (btn) {
        btn.disabled = b;
      });
    });
  }

  function finish(cart, action, source, msg) {
    return within(render(), 12000).then(function () {
      busy(false);
      if (msg) announce(msg);
      emit('asc:cart:updated', { cart: cart, action: action, source: source || null });
      return cart;
    });
  }

  function fail(err, items, source) {
    busy(false);
    emit('asc:cart:error', { message: err.message, items: items || null, source: source || null });
    bodies().forEach(function (b) {
      var live = b.querySelector('[data-asc-live]');
      if (live) live.textContent = err.message;
    });
    throw err;
  }

  function normItems(items) {
    if (!Array.isArray(items)) items = [items];
    var edd = document.querySelector('input[name="properties[Estimated between]"]');
    return items
      .filter(function (it) {
        return it && it.id;
      })
      .map(function (it) {
        var props = Object.assign({}, it.properties || {});
        if (edd && edd.value && !props['Estimated between']) props['Estimated between'] = edd.value;
        var out = { id: Number(it.id), quantity: Math.max(1, parseInt(it.quantity, 10) || 1) };
        if (Object.keys(props).length) out.properties = props;
        if (it.selling_plan) out.selling_plan = it.selling_plan;
        return out;
      });
  }

  function add(items, opts) {
    opts = opts || {};
    var list = normItems(items);
    if (!list.length) return Promise.reject(new Error('Nothing to add'));
    busy(true);
    return serial(function () {
      return req('cart/add.js', { items: list })
        .then(function () {
          return sync();
        })
        .then(function (cart) {
          return finish(cart, 'add', opts.source, 'Added to your cart');
        })
        .then(function (cart) {
          if (opts.open !== false) open();
          return cart;
        })
        .catch(function (e) {
          return fail(e, list, opts.source);
        });
    });
  }

  function update(key, quantity, opts) {
    opts = opts || {};
    var q = Math.max(0, parseInt(quantity, 10) || 0);
    busy(true);
    return serial(function () {
      return req('cart/change.js', { id: String(key), quantity: q })
        .then(function (c) {
          return sync(c);
        })
        .then(function (cart) {
          return finish(cart, q === 0 ? 'remove' : 'update', opts.source, q === 0 ? 'Removed from your cart' : 'Quantity updated');
        })
        .catch(function (e) {
          return render().then(function () {
            return fail(e, null, opts.source);
          });
        });
    });
  }

  function remove(key, opts) {
    return update(key, 0, opts);
  }

  function setCover(dockVariantId, years, opts) {
    opts = opts || {};
    var d = String(dockVariantId);
    busy(true);
    return serial(function () {
      return getCart()
        .then(function (cart) {
          var qty = 0;
          var label = null;
          cart.items.forEach(function (it) {
            if (isDock(it) && String(it.variant_id) === d) {
              qty += it.quantity;
              label = label || dockLabel(it, cfg.names);
            }
          });
          var covers = cart.items.filter(function (it) {
            return isCover(it) && it.properties && String(it.properties._asc_dock) === d;
          });
          if (Number(years) === 2) {
            if (!qty || !cfg.coverOffered) return cart;
            if (covers.length) return cart; // sync fixes the quantity
            return req('cart/add.js', { items: [{ id: Number(W_ID), quantity: qty, properties: coverProps(d, label) }] });
          }
          if (!covers.length) return cart;
          var updates = {};
          covers.forEach(function (c) {
            updates[c.key] = 0;
          });
          return req('cart/update.js', { updates: updates });
        })
        .then(function () {
          return sync();
        })
        .then(function (cart) {
          return finish(cart, 'cover', opts.source, Number(years) === 2 ? '2-year cover added' : '1-year cover, included');
        })
        .catch(function (e) {
          return render().then(function () {
            return fail(e, null, opts.source);
          });
        });
    });
  }

  // "Change model": swap a dock line to another variant, keep quantity, properties and its cover.
  function replaceDocks(key, newVariantId, opts) {
    opts = opts || {};
    var nid = String(newVariantId);
    busy(true);
    return serial(function () {
      var oldId;
      var hadCover = false;
      return getCart()
        .then(function (cart) {
          var line = cart.items.filter(function (it) {
            return it.key === key;
          })[0];
          if (!line) throw new Error('This product is no longer in your cart.');
          oldId = String(line.variant_id);
          if (oldId === nid) return cart;
          hadCover = cart.items.some(function (it) {
            return isCover(it) && it.properties && String(it.properties._asc_dock) === oldId;
          });
          var props = {};
          Object.keys(line.properties || {}).forEach(function (k) {
            if (line.properties[k] != null && line.properties[k] !== '') props[k] = line.properties[k];
          });
          var item = { id: Number(nid), quantity: line.quantity };
          if (Object.keys(props).length) item.properties = props;
          // add first: if the new model is sold out, nothing is lost
          return req('cart/add.js', { items: [item] }).then(function () {
            return req('cart/change.js', { id: key, quantity: 0 });
          });
        })
        .then(function () {
          return getCart();
        })
        .then(function (cart) {
          if (!hadCover || !cfg.coverOffered) return cart;
          var stillOld = cart.items.some(function (it) {
            return isDock(it) && String(it.variant_id) === oldId;
          });
          var newHas = cart.items.some(function (it) {
            return isCover(it) && it.properties && String(it.properties._asc_dock) === nid;
          });
          if (stillOld || newHas) return cart;
          var dockLine = cart.items.filter(function (it) {
            return isDock(it) && String(it.variant_id) === nid;
          })[0];
          var qty = dockLine ? dockLine.quantity : 1;
          return req('cart/add.js', {
            items: [{ id: Number(W_ID), quantity: qty, properties: coverProps(nid, dockLabel(dockLine || {}, cfg.names)) }],
          });
        })
        .then(function () {
          return sync();
        })
        .then(function (cart) {
          return finish(cart, 'update', opts.source, 'Model changed');
        })
        .catch(function (e) {
          return render().then(function () {
            return fail(e, null, opts.source);
          });
        });
    });
  }

  // Removes every line of one team offer (docks and trays sharing _team_id).
  function removeTeam(teamId, opts) {
    opts = opts || {};
    busy(true);
    return serial(function () {
      return getCart()
        .then(function (cart) {
          var updates = {};
          cart.items.forEach(function (it) {
            if (it.properties && String(it.properties._team_id) === String(teamId)) updates[it.key] = 0;
          });
          return req('cart/update.js', { updates: updates });
        })
        .then(function () {
          return sync();
        })
        .then(function (cart) {
          return finish(cart, 'remove', opts.source || 'cart', 'Team order removed');
        })
        .catch(function (e) {
          return render().then(function () {
            return fail(e, null, opts.source);
          });
        });
    });
  }

  function refresh() {
    return render();
  }

  function drawerEl() {
    return document.getElementById('AscCartDrawer');
  }

  function open(trigger) {
    bindUI();
    if (cfg.isCartPage) {
      var t = document.querySelector('[data-asc-cart-body][data-mode="page"] .ascc-title');
      if (t) {
        t.scrollIntoView({ block: 'start' });
        t.focus({ preventScroll: true });
      }
      emit('asc:cart:open', { __fromApi: true });
      return Promise.resolve();
    }
    var d = drawerEl();
    if (d && typeof d.open === 'function') d.open(trigger);
    // always refresh on open: the HTML may predate a change made elsewhere (a discount code, another tab, an app)
    render();
    // express buttons load on the first open (perf): fetch them if the drawer has none yet
    var wr = document.querySelector('[data-asc-cart-body][data-mode="drawer"] [data-ascc-region="wallets"]');
    var db = document.querySelector('[data-asc-cart-body][data-mode="drawer"]');
    // on phones they sit behind "More payment options" and load when that opens (fr1 #11c)
    if (wr && !wr.children.length && db && !db.classList.contains('is-empty') && !compactExpress()) render();
    return Promise.resolve();
  }

  function compactExpress() {
    return !!(window.matchMedia && window.matchMedia('(max-width: 749px), (max-height: 500px)').matches);
  }

  function close() {
    var d = drawerEl();
    if (d && typeof d.close === 'function') d.close();
  }

  /* ---------- watch cart changes made by other code (apps, Dawn forms) ---------- */

  var extTimer = null;
  var extBusy = false;
  function externalChange() {
    clearTimeout(extTimer);
    // mark busy at once (Checkout is disabled until the covers are re-checked), once per burst of changes
    if (!extBusy) {
      extBusy = true;
      busy(true);
    }
    extTimer = setTimeout(function () {
      extBusy = false;
      serial(function () {
        return sync().then(function (cart) {
          return finish(cart, 'external', null);
        });
      }).catch(function () {
        busy(false);
      });
    }, 250);
  }
  // cart writes, plus discount codes applied with GET /discount/<code> (teams preview): both change the cart's prices
  var CART_RE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)|\/discount\/[^/?#]+/;
  function isOurs(init) {
    var h = init && init.headers;
    if (!h) return false;
    if (typeof h.get === 'function') return h.get(MARK) === '1';
    return h[MARK] === '1';
  }
  window.fetch = function (input, init) {
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    var p = nativeFetch(input, init);
    if (CART_RE.test(url.split('#')[0]) && !isOurs(init)) {
      p.then(function (r) {
        if (r && r.ok) externalChange();
      }).catch(function () {});
    }
    return p;
  };
  var xo = XMLHttpRequest.prototype.open;
  var xs = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.open = function (m, url) {
    this.__ascCart = CART_RE.test(String(url || '').split('#')[0]);
    return xo.apply(this, arguments);
  };
  XMLHttpRequest.prototype.send = function () {
    if (this.__ascCart) {
      this.addEventListener('load', function () {
        if (this.status >= 200 && this.status < 300) externalChange();
      });
    }
    return xs.apply(this, arguments);
  };

  /* ---------- UI events (delegated, capture phase: other scripts on the page stop propagation of
     change/click events on some devices, e.g. iPhone Safari) ---------- */

  function cardOf(el) {
    return el.closest('[data-key]');
  }

  function onClick(e) {
    var t = e.target;
    if (!t.closest) return;
    var icon = !cfg.isCartPage && t.closest('#cart-icon-bubble');
    if (icon && document.getElementById('AscCartDrawer')) {
      e.preventDefault();
      open(icon);
      return;
    }
    var opener = t.closest('[data-asc-cart-open]');
    if (opener) {
      e.preventDefault();
      open(opener);
      return;
    }
    if (!t.closest('[data-asc-cart-body]') && !t.closest('#AscCartDrawer')) return;
    var xt = t.closest('[data-asc-express-toggle]');
    if (xt) {
      var foot = xt.closest('.ascc-foot');
      var openNow = !foot.classList.contains('is-express-open');
      foot.classList.toggle('is-express-open', openNow);
      xt.setAttribute('aria-expanded', openNow ? 'true' : 'false');
      var wr2 = foot.querySelector('[data-ascc-region="wallets"]');
      if (openNow && wr2 && !wr2.children.length) render();
      return;
    }
    var closer = t.closest('[data-asc-cart-close]');
    if (closer) {
      e.preventDefault();
      close();
      return;
    }
    var q = t.closest('[data-asc-qty]');
    if (q) {
      var card = cardOf(q);
      var cur = Number(card.getAttribute('data-quantity')) || 0;
      update(card.getAttribute('data-key'), cur + Number(q.getAttribute('data-asc-qty')), { source: 'cart' });
      return;
    }
    var rm = t.closest('[data-asc-remove]');
    if (rm) {
      var rc = cardOf(rm);
      remove(rc.getAttribute('data-key'), { source: 'cart' });
      return;
    }
    var cm = t.closest('[data-asc-change-model]');
    if (cm) {
      var panel = document.getElementById(cm.getAttribute('aria-controls'));
      var openNow = cm.getAttribute('aria-expanded') !== 'true';
      cm.setAttribute('aria-expanded', openNow ? 'true' : 'false');
      if (panel) {
        panel.hidden = !openNow;
        if (openNow) {
          var first = panel.querySelector('button:not([disabled])');
          if (first) first.focus();
        }
      }
      return;
    }
    var sw = t.closest('[data-asc-swap]');
    if (sw) {
      replaceDocks(cardOf(sw).getAttribute('data-key'), sw.getAttribute('data-asc-swap'), { source: 'cart' });
    }
  }

  function onChange(e) {
    var t = e.target;
    if (!t.closest || !t.closest('[data-asc-cart-body]')) return;
    if (t.matches('[data-asc-cover-radio]')) {
      var fs = t.closest('[data-asc-cover]');
      fs.querySelectorAll('.ascc-tile').forEach(function (l) {
        l.classList.toggle('is-on', l.contains(t));
      });
      setCover(fs.getAttribute('data-dock'), t.value, { source: 'cart' });
      return;
    }
    if (t.matches('[data-asc-qty-input]')) {
      var card = cardOf(t);
      var v = parseInt(t.value, 10);
      if (isNaN(v) || v < 0) v = 0;
      if (String(v) !== card.getAttribute('data-quantity')) update(card.getAttribute('data-key'), v, { source: 'cart' });
    }
  }

  function onKeydown(e) {
    if (e.key === ' ' && !cfg.isCartPage && e.target.closest && e.target.closest('#cart-icon-bubble')) {
      e.preventDefault();
      open(e.target.closest('#cart-icon-bubble'));
      return;
    }
    if (e.key === 'Enter' && e.target.matches && e.target.matches('[data-asc-qty-input]')) {
      e.preventDefault();
      e.target.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  // Checkout: make sure the cover quantities are right before leaving for checkout.
  function onSubmit(e) {
    var f = e.target;
    if (!f.matches || !f.matches('[data-asc-checkout-form]')) return;
    if (f.__ascGo) return;
    e.preventDefault();
    var btn = f.querySelector('[name="checkout"]');
    if (btn) btn.setAttribute('aria-busy', 'true');
    serial(function () {
      return sync();
    })
      .catch(function () {})
      .then(function () {
        f.__ascGo = true;
        var hidden = document.createElement('input');
        hidden.type = 'hidden';
        hidden.name = 'checkout';
        hidden.value = '';
        f.appendChild(hidden);
        HTMLFormElement.prototype.submit.call(f);
      });
  }

  // Listener binding is idempotent (same function + options = no duplicate), so it is repeated at boot,
  // on load, after a delay, on open and after each render: on iPhone Safari something on the page drops
  // document listeners registered early (seen 2026-10-19 in WebKit QA), which made the cart dead to taps.
  function bindUI() {
    document.addEventListener('click', onClick, true);
    document.addEventListener('change', onChange, true);
    document.addEventListener('keydown', onKeydown, true);
    document.addEventListener('submit', onSubmit, false);
  }
  bindUI();
  window.addEventListener('load', bindUI);
  window.addEventListener('pageshow', bindUI);
  setTimeout(bindUI, 2500);

  // Anyone may dispatch asc:cart:open to open the drawer; our own announcements carry __fromApi.
  document.addEventListener('asc:cart:open', function (e) {
    if (e.detail && e.detail.__fromApi) return;
    open();
  });

  /* ---------- export ---------- */

  var api = {
    __asc: 1,
    add: add,
    update: update,
    remove: remove,
    setCover: setCover,
    replaceDocks: replaceDocks,
    removeTeam: removeTeam,
    get: getCart,
    open: open,
    close: close,
    refresh: refresh,
    sync: function () {
      busy(true);
      return serial(function () {
        return sync().then(function (c) {
          return finish(c, 'sync', null);
        });
      });
    },
    warrantyId: Number(W_ID),
    _planSync: planSync,
    _summarize: summarize,
  };
  window.ascCart = api;

  // Others may dispatch these to drive the drawer.
  document.addEventListener('asc:cart:refresh', function () {
    render();
  });

  function boot() {
    bindUI();
    bodies().forEach(enhance);
    if (cfg.checkOnLoad) {
      serial(function () {
        return getCart().then(function (c) {
          var plan = planSync(c, syncOpts());
          if (!Object.keys(plan.updates).length && !plan.adds.length) return c;
          busy(true);
          return sync(c).then(function (cart) {
            return finish(cart, 'sync', null);
          });
        });
      }).catch(function () {});
    }
    var q = window.ascCartQueue;
    window.ascCartQueue = {
      push: function (fn) {
        try {
          fn(api);
        } catch (e) {
          console.error(e);
        }
      },
    };
    if (Array.isArray(q))
      q.forEach(function (fn) {
        window.ascCartQueue.push(fn);
      });
    emit('asc:cart:ready', { api: api });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
